import { Box, Center, Heading } from "@chakra-ui/react"
import {
  AppEntityHydrated,
  BlockEntityHydrated,
  PageEntityHydrated,
} from "@jackfruit/common"
import React from "react"
import { useSearchParam } from "react-use"
import HeroBlock from "./blocks/block-hero/HeroBlock"
import StoreSearchBlockWrapper from "./blocks/block-store-search/StoreSearchBlockWrapper"
import CartBlockWrapper from "./blocks/CartBlockWrapper"
import CheckoutBlockWrapper from "./blocks/CheckoutBlockWrapper"
import FooterBlock from "./blocks/FooterBlock"
import FulfillmentBlockWrapper from "./blocks/FulfillmentBlockWrapper"
import GraphBlock from "./blocks/GraphBlock"
import HeaderBlock from "./blocks/HeaderBlock"
import HtmlBlock from "./blocks/HtmlBlock"
import ImageCarouselBlock from "./blocks/ImageCarouselBlock"
import ImageGridBlock from "./blocks/ImageGridBlock"
import ImageUploadBlockWrapper from "./blocks/ImageUploadBlockWrapper"
import OrderCompleteBlockWrapper from "./blocks/OrderCompleteBlockWrapper"
import OrderThankYouBlock from "./blocks/OrderThankYouBlock"
import PhotosUploadedBlockWrapper from "./blocks/PhotosUploadedBlockWrapper"
import ProductFeedBlock from "./blocks/product-feed/ProductFeedBlock"
import ProductGridBlock from "./blocks/ProductGridBlock"
import RichTextBlock from "./blocks/RichTextBlock"
import SocialMediaBlock from "./blocks/SocialMediaBlock"
import SubpageGridBlock from "./blocks/SubpageGridBlock"
import TemplateBlockWrapper from "./blocks/TemplateBlockWrapper"
import VideoBlock from "./blocks/VideoBlock"
import FloatingActionSensor from "./floating-actions/FloatingActionSensor"
import ScrollElementWrapper from "./scroll-wrappers/ScrollElementWrapper"

interface Props {
  block: BlockEntityHydrated
  config: AppEntityHydrated
  page: PageEntityHydrated
}

const PageBlock: React.FC<Props> = ({ block, config, page }) => {
  let blockToReturn: any = null

  const blockTrackingId = `p-${block.type.toLowerCase()}-${block.id}`

  const hideHeader = useSearchParam("hideHeader") === "1"
  const hideFooter = useSearchParam("hideFooter") === "1"
  const focusMode = useSearchParam("focusMode") === "1"

  const hasFloatingCallToAction =
    page.pageInteractions && page.pageInteractions.length > 0

  switch (block.type) {
    case "header":
      const headerConfig = config.blockHeaders.find(
        header => Number(header.id) === Number(block.headerId)
      )!

      blockToReturn =
        hideHeader || focusMode ? (
          <></>
        ) : (
          <HeaderBlock
            config={headerConfig}
            pages={config.pages}
            appConfig={config}
          />
        )
      break
    case "rich-text":
      blockToReturn = <RichTextBlock config={block.richText} />
      break
    case "html":
      blockToReturn = <HtmlBlock config={block.html} appConfig={config} />
      break
    case "cart":
      blockToReturn = <CartBlockWrapper config={block.cart} />
      break
    case "image-upload":
      blockToReturn = <ImageUploadBlockWrapper config={block.imageUpload} />
      break
    case "fulfillment":
      blockToReturn = (
        // using FulfillmentBlockV2 in wrapper for VNZ demo
        <FulfillmentBlockWrapper config={block.fulfillment} page={page} />
      )
      break
    case "checkout":
      blockToReturn = (
        <CheckoutBlockWrapper config={block.checkout} pages={config.pages} />
      )
      break
    case "image-grid":
      blockToReturn = <ImageGridBlock config={block.imageGrid} />
      break
    case "store-search":
      blockToReturn = <StoreSearchBlockWrapper config={block.storeSearch} />
      break
    case "footer":
      const footerConfig = config.blockFooters.find(
        footer => Number(footer.id) === Number(block.footerId)
      )!

      blockToReturn =
        hideFooter || focusMode ? (
          <></>
        ) : (
          <FooterBlock config={footerConfig} appConfig={config} />
        )
      break
    case "order-complete":
      blockToReturn = <OrderCompleteBlockWrapper config={block.orderComplete} />
      break
    case "photos-uploaded":
      blockToReturn = (
        <PhotosUploadedBlockWrapper config={block.photosUploaded} />
      )
      break
    case "social-media":
      blockToReturn = (
        <SocialMediaBlock config={block.socialMedia} appConfig={config} />
      )
      break
    case "order-thank-you":
      blockToReturn = (
        <OrderThankYouBlock appConfig={config} config={block.orderThankYou} />
      )
      break
    case "template":
      blockToReturn = <TemplateBlockWrapper config={block.template} />
      break
    case "subpage-grid":
      blockToReturn = (
        <SubpageGridBlock config={block.subpageGrid} pages={page.subpages!} />
      )
      break
    case "product-grid":
      blockToReturn = <ProductGridBlock config={block.productGrid} />
      break
    case "graph":
      blockToReturn = <GraphBlock config={block.graph} />
      break
    case "video":
      blockToReturn = <VideoBlock config={block.video} />
      break
    case "image-carousel":
      blockToReturn = <ImageCarouselBlock config={block.imageCarousel} />
      break
    case "product-feed":
      blockToReturn = (
        <ProductFeedBlock pageId={page.id} config={block.productFeed} />
      )
      break
    case "hero":
      blockToReturn = <HeroBlock config={block.hero} />
      break
  }

  if (blockToReturn) {
    return (
      <Box
        className={[
          "block-container",
          `block-${block.type.toLowerCase()}`,
        ].join(" ")}
        id={blockTrackingId}
      >
        <ScrollElementWrapper id={block.elementId} name={block.type}>
          {hasFloatingCallToAction ? (
            <FloatingActionSensor id={blockTrackingId}>
              {blockToReturn}
            </FloatingActionSensor>
          ) : (
            blockToReturn
          )}
        </ScrollElementWrapper>
      </Box>
    )
  }

  //Unknown block
  return (
    <Box>
      <Center>
        <Heading>Unknown Block: {block.type}</Heading>
      </Center>
    </Box>
  )
}

export default PageBlock
